import { Box, Typography } from "@mui/material";
import EducationShell from "../../components/education/EducationShell";

// ─── Palette — matches EducationLanding.jsx / TypesLanding.jsx ───────────────
const INK = "#0F172A";
const SLATE = "#475569";
const MUTED = "#64748B";
const BORDER = "#E2E8F0";
const ACCENT = "#0F766E";
const ACCENT_SOFT = "#F0FDFA";
const PAPER = "#FFFFFF";
const CANVAS = "#F8FAFC";

const TOPICS = [
  {
    title: "Diet & nutrition",
    body: "What you eat plays a direct role in slowing CKD and managing its complications. A renal dietitian can help tailor guidance to your specific stage and labs, but general themes include:",
    points: [
      "Sodium moderation to help control blood pressure and fluid retention",
      "Protein intake guided by your care team — needs shift depending on your CKD stage and whether you're on dialysis",
      "Potassium and phosphorus awareness, especially from Stage 3 onward or if your labs run high",
      "Staying within your fluid allowance if you've been given one, particularly on dialysis",
    ],
  },
  {
    title: "Physical activity",
    body: "Regular activity helps manage blood pressure, blood sugar, and cardiovascular risk — all of which directly affect kidney health.",
    points: [
      "Aim for activity you can sustain — walking, swimming, and cycling are all well-tolerated by most CKD patients",
      "Check with your care team before starting a new routine if you're on dialysis or have significant cardiovascular involvement",
      "Building activity into your dialysis schedule (if applicable) can help manage fatigue between sessions",
    ],
  },
  {
    title: "Mental health & coping",
    body: "A CKD diagnosis — and especially a shift to dialysis — is a major life change. It's common to experience anxiety, grief, or depression alongside the physical symptoms.",
    points: [
      "Ask your care team for a referral to a mental health professional who has experience with chronic illness",
      "Peer support from others managing CKD can help normalize the day-to-day adjustments",
      "Depression is common among people with chronic illness and can make it harder to manage your kidney disease — it's worth naming and addressing directly with your care team, not just pushing through",
    ],
  },
  {
    title: "Work & daily life",
    body: "Many people continue working and managing daily responsibilities throughout CKD, though adjustments are often needed, especially around dialysis scheduling.",
    points: [
      "Talk to your employer early about scheduling needs if you start dialysis — many employers can accommodate treatment times",
      "Know your rights: conditions like CKD may qualify for workplace accommodations",
      "Home dialysis modalities (like peritoneal dialysis) offer more schedule flexibility for some patients than in-center hemodialysis",
    ],
  },
  {
    title: "Caregiving & support",
    body: "Whether you're a patient or supporting one, CKD affects the whole household — especially around dialysis schedules, dietary changes, and appointments.",
    points: [
      "Caregivers benefit from the same education resources — understanding CKD stages, diet, and treatment helps them support day-to-day decisions",
      "Coordinate transportation early if dialysis is part of the picture — NephroTrax's NEMT partnership can help arrange recurring rides",
      "Don't hesitate to ask your care team what support services (social work, financial counseling) are available at your dialysis center or clinic",
    ],
  },
  {
    title: "Family planning & genetics",
    body: "For hereditary causes of CKD like polycystic kidney disease, family planning conversations are often part of ongoing care.",
    points: [
      "Genetic counseling can clarify inheritance risk for hereditary causes like ADPKD (50% risk per child)",
      "Pregnancy with CKD requires closer monitoring and often a joint plan between your nephrologist and OB",
      "Family members of patients with hereditary CKD causes may want to discuss screening with their own doctor",
    ],
  },
];

export default function LivingWithCKD() {
  return (
    <EducationShell breadcrumbs={[{ label: "Learn", path: "/education" }, { label: "Living with CKD" }]}>
      <Box sx={{ fontFamily: "'DM Sans', sans-serif", bgcolor: PAPER }}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Fraunces:ital,wght@0,300;0,600;1,300&display=swap');
        `}</style>

        {/* Intro */}
        <Box sx={{ px: { xs: 3, md: 8 }, pt: { xs: 5, md: 7 }, pb: { xs: 4, md: 5 }, borderBottom: `1px solid ${BORDER}` }}>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: ACCENT, mb: 1.5 }}>
            Living with CKD
          </Typography>
          <Typography sx={{ fontFamily: "'Fraunces', serif", fontSize: { xs: "1.7rem", md: "2.3rem" }, fontWeight: 600, color: INK, mb: 2, maxWidth: 640 }}>
            Day-to-day life with chronic kidney disease
          </Typography>
          <Typography sx={{ fontSize: "0.95rem", color: SLATE, maxWidth: 620, lineHeight: 1.7 }}>
            CKD touches far more than lab values — diet, activity, work, mental health, and family all factor in.
            This is a starting point for the everyday questions that come up alongside the clinical ones.
          </Typography>
        </Box>

        {/* Topic sections */}
        <Box sx={{ px: { xs: 3, md: 8 }, py: { xs: 6, md: 8 } }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {TOPICS.map((topic) => (
              <Box key={topic.title} sx={{ border: `1px solid ${BORDER}`, borderRadius: "12px", p: { xs: 3, md: 4 }, bgcolor: PAPER }}>
                <Typography sx={{ fontFamily: "'Fraunces', serif", fontSize: { xs: "1.15rem", md: "1.35rem" }, fontWeight: 600, color: INK, mb: 1.5 }}>
                  {topic.title}
                </Typography>
                <Typography sx={{ fontSize: "0.92rem", color: SLATE, lineHeight: 1.75, mb: 2.5, maxWidth: 760 }}>
                  {topic.body}
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {topic.points.map((point, i) => (
                    <Box key={i} sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: ACCENT, mt: 0.9, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: "0.88rem", color: "#374151", lineHeight: 1.7, maxWidth: 740 }}>
                        {point}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>

          {/* Crisis-adjacent note — mental health section references depression;
              keep this understated and not diagnostic, just a pointer to real support. */}
          <Box sx={{ mt: 4, p: 3, borderRadius: "10px", bgcolor: ACCENT_SOFT, border: `1px solid ${BORDER}` }}>
            <Typography sx={{ fontSize: "0.85rem", color: SLATE, lineHeight: 1.7 }}>
              If you're feeling overwhelmed or persistently low, talk to your care team — they can connect you
              with mental health support suited to living with a chronic condition. If you're in crisis, the
              988 Suicide & Crisis Lifeline (call or text 988) is available 24/7 in the U.S.
            </Typography>
          </Box>
        </Box>
      </Box>
    </EducationShell>
  );
}